/**@author */
import { testProps } from "app/shared/util/test-props";
import React, { useMemo } from "react";
import { RegisterOptions, useController } from "react-hook-form";
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  StyleProp,
  StyleSheet,
  TextInputEndEditingEventData,
  TextInputKeyPressEventData,
  TextStyle,
  useWindowDimensions,
} from "react-native";
import { HelperText, TextInput, useTheme } from "react-native-paper";
import { Block } from "../../blocks/block";

interface InputTextMultilineProps {
  control: any;
  name: string;
  inputProps?: object;
  style?: StyleProp<TextStyle>;
  label?: string;
  note?: string;
  onChangedText?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  keyboardType?: KeyboardTypeOptions;
  rules?: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  onKeyPress?: (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  rightContent?: JSX.Element;
  rightAffix?: boolean;
  maxLength?: number;
}

export const InputTextMultiline = (props: InputTextMultilineProps) => {
  const {
    control,
    name,
    inputProps,
    style = {},
    label,
    note,
    rules,
    onChangedText = () => {},
    onBlur = () => {},
    onFocus = () => {},
    keyboardType = "default",
    onKeyPress = () => {},
    rightContent,
    rightAffix,
    maxLength = 100,
  } = props;
  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: "",
  });
  const { colors, fonts } = useTheme();
  const { width } = useWindowDimensions();
  // Ngăn ngừa re-render khi bắt sự kiện onBlur;
  const [valueChanged, setValueChanged] = React.useState(false);
  const [heightInput, setHeightInput] = React.useState<number | undefined>();
  const [isFocus, setFocus] = React.useState<boolean>(false);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        inputStyle: {
          flex: 1,
          color: colors.accent,
          backgroundColor: colors.backgroundForm,
          fontWeight: "700",
          textShadowColor: "black",
          height: heightInput,
        },
        textAffix: { fontFamily: fonts.regular.fontFamily, fontSize: 8 },
      }),
    [colors, fonts, heightInput],
  );

  const changeFocus = (value: boolean) => {
    if (value === true) {
      setFocus(true);
    } else {
      setFocus(false);
    }
  };

  const onChangedTextInput = (value: string) => {
    field.onChange(value);
    onChangedText(value);
    setValueChanged(true);
    if (!value || value.length === 0) setHeightInput(undefined);
  };

  const onBlurTextInput = () => {
    setHeightInput(undefined);
    field.onBlur();
    if (valueChanged) {
      onBlur();
    }
    changeFocus(false);
  };

  const onFocusTextInput = () => {
    onFocus();
    changeFocus(true);
  };

  const onEndEditingTextInput = (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
    console.log("onEndEditingTextInput", e.nativeEvent.text);
  };

  const onKeyPressTextInput = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    onKeyPress(e);
    if (e.nativeEvent.key === "Tab") {
      e.preventDefault();
    }
  };

  React.useEffect(() => {
    setHeightInput(undefined);
  }, [width]);

  const numberDescription = <TextInput.Affix textStyle={styles.textAffix} text={field.value?.length + "/" + maxLength} />;

  return (
    <>
      <Block row center>
        <TextInput
          mode={"flat"}
          outlineColor={"transparent"}
          theme={{
            colors: {
              primary: colors.accent,
              text: colors.accent,
            },
          }}
          style={[styles.inputStyle, style]}
          keyboardType={keyboardType}
          ref={field.ref}
          onKeyPress={onKeyPressTextInput}
          value={field.value}
          onBlur={onBlurTextInput}
          onFocus={onFocusTextInput}
          onChangeText={onChangedTextInput}
          onEndEditing={onEndEditingTextInput}
          label={label}
          error={invalid}
          autoCompleteType="off"
          right={rightAffix ? numberDescription : null}
          {...testProps(name)}
          {...inputProps}
          numberOfLines={1}
          onContentSizeChange={(e) => {
            setHeightInput(e.nativeEvent.contentSize.height);
          }}
        />
        <>{rightContent}</>
      </Block>
      {isFocus && note?.length && !invalid && (
        <HelperText type="info" visible={!invalid}>
          {note}
        </HelperText>
      )}
      {invalid && error?.message && (
        <HelperText type="error" visible={invalid}>
          {error?.message}
        </HelperText>
      )}
    </>
  );
};
